// Shapes returned by the HTTP API (/api/v1). Shared by the server, the web app and any
// bot/client written in TypeScript. Times are epoch milliseconds.
import type { BlockStatus, PlanIssue, PlannerSettings, TaskSummary, TimeWindow, TravelMode } from './types';

export type ItemKind = 'event' | 'deadline' | 'routine' | 'task' | 'travel' | 'sleep';
export type Category = 'work' | 'uni' | 'personal' | 'other';

/** Everything on the calendar – fixed events and planner blocks – in one shape. */
export interface CalendarItem {
  id: string;
  kind: ItemKind;
  title: string;
  start: number;
  end: number;
  allDay: boolean;
  date: string;
  color: string;
  category: Category | null;
  placeId: string | null;
  /** raw location text from the source calendar */
  location: string | null;
  description: string | null;
  /** event: source id; routine/task: routine/task id */
  refId: string | null;
  sourceId: string | null;
  status: BlockStatus | null;
  flags: string[];
  busy: boolean;
  /** manual events can be edited; synced ones are read-only */
  editable: boolean;
  /** module code (FEEG1002…) for uni events and study sessions, when known */
  module?: string | null;
  // travel blocks
  fromPlaceId?: string | null;
  toPlaceId?: string | null;
  targetId?: string | null;
  mode?: string | null;
  directionsUrl?: string | null;
}

export interface PlanResponse {
  from: number;
  to: number;
  timezone: string;
  revision: number;
  generatedAt: number | null;
  items: CalendarItem[];
  issues: PlanIssue[];
}

export interface Place {
  id: string;
  name: string;
  address: string | null;
  lat: number | null;
  lng: number | null;
  kind: 'home' | 'work' | 'uni' | 'gym' | 'other';
  travelMode: TravelMode | null;
  createdAt: number;
}

export interface Source {
  id: string;
  name: string;
  kind: 'ics' | 'manual' | 'bridge';
  url: string | null;
  category: Category;
  color: string;
  enabled: boolean;
  defaultPlaceId: string | null;
  bufferBeforeMin: number;
  syncIntervalMin: number;
  /** Rename synced events, e.g. "Shift · {title}" turns "Bar" into "Shift · Bar". */
  titleTemplate: string | null;
  lastSyncedAt: number | null;
  lastError: string | null;
  lastEventCount: number | null;
  createdAt: number;
}

export interface PlaceRule {
  id: string;
  sourceId: string | null;
  pattern: string;
  placeId: string | null;
  online: boolean;
  position: number;
}

export interface EventDTO {
  id: string;
  sourceId: string;
  uid: string | null;
  title: string;
  description: string | null;
  start: number;
  end: number;
  allDay: boolean;
  location: string | null;
  placeId: string | null;
  busy: boolean;
  hidden: boolean;
}

export interface RoutineDTO {
  id: string;
  name: string;
  color: string | null;
  enabled: boolean;
  durationMin: number;
  minDurationMin: number | null;
  days: number[];
  windows: TimeWindow[];
  preferredTime: string | null;
  priority: number;
  splittable: boolean;
  minChunkMin: number | null;
  placeId: string | null;
  bufferAfterMin: number;
  notes: string | null;
  position: number;
  /** What the Assistant treats this routine as. null = guess from the name. */
  activity: RoutineActivity | null;
}

export type TaskStatus = 'todo' | 'in_progress' | 'done' | 'cancelled';

export interface TaskDTO {
  id: string;
  source: string;
  externalId: string | null;
  title: string;
  notes: string | null;
  module: string | null;
  url: string | null;
  deadline: number | null;
  earliestStart: number | null;
  estimatedMin: number;
  spentMin: number;
  remainingMin: number;
  status: TaskStatus;
  priority: number;
  minChunkMin: number;
  maxChunkMin: number;
  maxPerDayMin: number | null;
  splittable: boolean;
  placeId: string | null;
  color: string | null;
  createdAt: number;
  updatedAt: number;
  completedAt: number | null;
  /** from the latest plan */
  plan: TaskSummary | null;
}

export interface TravelSettings {
  provider: 'auto' | 'google' | 'ors' | 'estimate';
  defaultMode: TravelMode;
  googleApiKey: string | null;
  orsApiKey: string | null;
}

export interface NotifySettings {
  ntfyUrl: string | null;
  ntfyToken: string | null;
  webPush: boolean;
  leaveLeadMin: number;
  startReminders: boolean;
  eventReminderMin: number | null;
  morningBriefTime: string | null;
}

export interface TaskSettings {
  /** Count planned task sessions as done once they've passed (you can mark them missed). */
  autoCreditSessions: boolean;
}

export interface FeedSettings {
  token: string;
}

export interface BridgeSettings {
  enabled: boolean;
  inPath: string | null;
  outPath: string | null;
  /** School Manager's context.json (modules, study notes, glossary) – powers the Assistant. */
  contextPath: string | null;
  /** School Manager's dashboard.json – powers the Everything page. */
  dashboardPath: string | null;
  lastReadAt: number | null;
  lastWriteAt: number | null;
  lastContextAt: number | null;
  lastDashboardAt: number | null;
  lastError: string | null;
}

/** How Tempo reaches Claude: the Claude Code CLI on your PC (your Claude subscription) or an API key. */
export type ClaudeEngine = 'cli' | 'api';

export interface AssistantSettings {
  engine: ClaudeEngine;
  /** Only for engine "api". Masked when sent to the client. Also read from ANTHROPIC_API_KEY. */
  claudeApiKey: string | null;
  model: string;
  /** Store lecture transcripts on the server (off by default). */
  keepTranscripts: boolean;
  /** How often the live explainer asks Claude about the latest part of a lecture. */
  explainIntervalSec: number;
  /** Safety cap on explainer calls per day. */
  dailyExplainCap: number;
}

export interface AllSettings {
  planner: PlannerSettings;
  travel: TravelSettings;
  notify: NotifySettings;
  tasks: TaskSettings;
  bridge: BridgeSettings;
  assistant: AssistantSettings;
}

// ───────────────────────────── Assistant ─────────────────────────────

export type RoutineActivity = 'cooking' | 'gym' | 'study' | 'rest' | 'other';
export const ROUTINE_ACTIVITIES: RoutineActivity[] = ['cooking', 'gym', 'study', 'rest', 'other'];

/**
 * Models offered for the lecture explainer. `note` is for an API key (rough cost at ~1 explanation a minute),
 * `planNote` for the CLI, where calls come out of your Claude plan's usage limits.
 */
export const CLAUDE_MODELS = [
  { id: 'claude-haiku-5-5', label: 'Claude Haiku 5.5 (default)', note: 'quick, keeps up with the lecture · a few cents per lecture hour', planNote: 'quick, keeps up with the lecture · uses very little of your plan' },
  { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5', note: 'more thorough · roughly $0.50 per lecture hour', planNote: 'more thorough · uses more of your plan' },
  { id: 'claude-opus-5-5', label: 'Claude Opus 5.5', note: 'most thorough · roughly $1 per lecture hour', planNote: 'most thorough · uses the most of your plan' },
] as const;

/** Whether live explanations can run right now, and if not, what to do about it. */
export interface ClaudeStatus {
  engine: ClaudeEngine;
  /** cli: Claude runs on this server's machine ("local") or on your PC through the relay ("relay"). */
  where: 'local' | 'relay' | null;
  ready: boolean;
  problem: 'signin' | 'missing' | 'relay_offline' | 'no_key' | null;
  message: string;
  /** cli: the command that signs the CLI in (run it once on your PC). */
  signInCommand: string | null;
  cliVersion: string | null;
}

/** What you should be doing right now, as the Assistant sees it. */
export type ActivityKind =
  | 'lecture'
  | 'tutorial'
  | 'lab'
  | 'study'
  | 'travel'
  | 'cooking'
  | 'gym'
  | 'shift'
  | 'sleep'
  | 'rest'
  | 'routine'
  | 'event'
  | 'free';

/**
 * Email that needs you, from School Manager's inbox triage (context.json → email). Display only: Tempo never
 * sends or replies – School Manager sends a drafted reply after you approve it in Claude ("send <draft id>").
 */
export interface EmailItem {
  id: string;
  /** 1 = needs you within about a day, 2 = within a few days, 3 = worth knowing. */
  priority: number;
  from: string | null;
  subject: string | null;
  /** One plain sentence: who wants what, by when. */
  summary: string | null;
  replyBy: string | null;
  date: string | null;
  /** When School Manager flagged it – what counts as "new" for pop-ups. */
  triagedAt: string | null;
  accountLabel: string | null;
  /** Webmail link (https only). */
  url: string | null;
  /** A recommended reply waiting in Drafts, with its text as School Manager wrote it. */
  draft: { id: string; sendPhrase: string; text: string | null } | null;
}

export interface EmailContext {
  checkedAt: string | null;
  needsYou: EmailItem[];
  worthKnowing: EmailItem[];
  /** Drafts not tied to a needsYou email. */
  draftsWaiting: { id: string; subject: string | null; accountLabel: string | null; sendPhrase: string }[];
}

export interface GlossaryEntry {
  term: string;
  aliases: string[];
  status: 'learned' | 'introduced' | string;
  definition: string;
  formula: string | null;
  modules: string[];
  firstCovered: string | null;
}

export interface ModuleContext {
  code: string;
  name: string | null;
  lecturer: string | null;
  assessmentSummary: string | null;
  nextLectureHint: string | null;
  /** false: the module has no lectures (its timetabled "L" sessions are e.g. unit tests). */
  recordsLectures: boolean | null;
  recentLectures: { title: string; date: string | null; summary: string | null; keyConcepts: string[]; whatNext: string | null; vaultPath: string | null }[];
  openWork: { id: string; title: string; due: string | null; kind: string | null }[];
  bring: Record<string, string[]>;
}

export interface StudyContext {
  requestId: string | null;
  title: string;
  module: string | null;
  notes: string | null;
  steps: string[];
  materials: { label: string; url: string | null; vaultPath: string | null; openUrl: string | null }[];
  relatedConcepts: string[];
}

export interface NowContext {
  now: number;
  activity: ActivityKind;
  /** The block you're in (null when free). */
  item: CalendarItem | null;
  /** First thing after the current block. */
  next: CalendarItem | null;
  nextUp: NextUp;
  tomorrowFirst: CalendarItem | null;
  module: ModuleContext | null;
  study: StudyContext | null;
  task: { id: string; title: string; source: string; module: string | null; remainingMin: number; url: string | null; externalProgress: boolean } | null;
  routine: { id: string; name: string; notes: string | null } | null;
  /** Things to take (for travel: to what you're heading to). */
  bring: string[];
  /** "Uni Vault" etc. – for obsidian:// links. */
  vault: string | null;
  /** Email that needs you (School Manager). The Assistant decides when it's worth showing. */
  email: EmailContext | null;
}

export interface ExplainResult {
  summary: string;
  concepts: { term: string; explanation: string; buildsOn: string[] }[];
  checkQuestion: string | null;
  model: string;
  at: number;
}

export interface LectureMark {
  id: string;
  eventId: string | null;
  eventUid: string | null;
  module: string | null;
  title: string | null;
  at: number;
  offsetSec: number | null;
  note: string | null;
  excerpt: string | null;
  createdAt: number;
}

/** One leg of a journey, e.g. "walk 6 min" or "bus U1C 16:32 Portswood Broadway → Central Station". */
export interface JourneyStep {
  mode: 'walk' | 'transit' | 'drive' | 'cycle';
  minutes: number;
  /** transit only */
  line?: string | null;
  vehicle?: string | null;
  headsign?: string | null;
  from?: string | null;
  to?: string | null;
  departAt?: number | null;
  arriveAt?: number | null;
  stops?: number | null;
}

/** A specific, timed journey (from Google Maps when a key is set). */
export interface Journey {
  fromLive: boolean;
  minutes: number;
  leaveAt: number | null;
  arriveAt: number | null;
  steps: JourneyStep[];
  provider: string;
}

export interface NextUp {
  now: number;
  item: CalendarItem | null;
  travel: CalendarItem | null;
  leaveAt: number | null;
  travelMin: number | null;
  mode: string | null;
  directionsUrl: string | null;
  /** Step-by-step version of the next journey, when available (public transport times etc.). */
  journey: Journey | null;
  live: {
    fromLocation: boolean;
    locationAge: number | null;
    travelMin: number | null;
    leaveAt: number | null;
    distanceM: number | null;
    arrived: boolean;
  } | null;
}

export interface ApiKeyDTO {
  id: string;
  name: string;
  prefix: string;
  createdAt: number;
  lastUsedAt: number | null;
}

export interface WebhookDTO {
  id: string;
  url: string;
  events: string[];
  enabled: boolean;
  lastStatus: number | null;
  lastError: string | null;
  lastDeliveredAt: number | null;
  createdAt: number;
}

export const WEBHOOK_EVENTS = ['plan.updated', 'task.at_risk', 'task.completed', 'sync.failed'] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];

export const KIND_COLORS: Record<string, string> = {
  work: '#f59e0b',
  uni: '#3b82f6',
  personal: '#ec4899',
  other: '#64748b',
  routine: '#10b981',
  task: '#8b5cf6',
  travel: '#94a3b8',
  sleep: '#334155',
  deadline: '#ef4444',
};

// ───────────────────── Everything (School Manager's dashboard.json, sm-dashboard/1) ─────────────────────

/** ok | overdue (it didn't run when it should have) | failed | signed out | missing | not yet | not set up */
export type CheckState = string;

/** Has it been checked? One per source School Manager watches (Blackboard, each inbox, Panopto, the calendar). */
export interface DashboardSource {
  key: string;
  label: string;
  checkedAt: string | null;
  state: CheckState;
  detail: string | null;
}

export interface DashboardRoutine {
  key: string;
  label: string;
  state: CheckState;
  lastOk: string | null;
  lastMessage: string | null;
  shouldHaveRun: string | null;
}

/** What School Manager did, newest first. */
export interface DashboardActivity {
  at: string | null;
  label: string;
  status: 'ok' | 'warn' | 'fail' | string;
  message: string | null;
}

export interface DashboardItem {
  id: string;
  kind: string | null;
  module: string | null;
  title: string;
  due: string | null;
  status: 'open' | 'done' | 'dropped' | string;
  priority: number | null;
  url: string | null;
  bbStatus: string | null;
  notesStatus: string | null;
  mark: string | null;
  estMinutes: number | null;
  minutesLeft: number | null;
  overdue: boolean;
  steps: string[];
  /** Study time Tempo has planned for it. */
  booked: { start: string; end: string; status: string | null }[];
  notes: string | null;
  location: string | null;
  source: string | null;
  /** Work left, but no study time booked from now on. */
  noTimeBooked: boolean;
  /** A change made in Tempo that School Manager hasn't picked up yet. */
  pending: SmActionType | null;
  /** What it is: what it asks, which session, what to bring (School Manager's description). */
  about: string | null;
  /** The documents: the sheet itself (a vault file served by /api/v1/school/file) and web pages such as Blackboard. */
  links: DashboardLink[];
  /** Vault lecture and concept notes to revise from. */
  related: DashboardRelated[];
  /** Formulas the work needs, shown in a box. `expr` uses X_Y for subscripts and x^2 for powers. */
  formulas: { name: string | null; expr: string }[];
  /** Hints, one per question or part; shown folded. */
  hints: { label: string; text: string }[];
  /** Answers (one per line); shown only when asked for. */
  answers: string | null;
  /** The session it's for, e.g. "Tutorial · today 16:00–17:00 · B176/1125". */
  session: string | null;
  /** What to bring to that session. */
  bring: string[];
  /** A guided way through the vault material for it (opened only when asked, in the Notes tabs). */
  guide: StudyGuide | null;
  /** Part of the red-alert catch-up plan. */
  catchup: boolean;
}

/** One step of a study guide: what to look at, in order. */
export interface StudyGuideStep {
  /** "read" (a lecture note, at the sections listed), "concepts", "slides", "self-test", "do", "review". */
  kind: string;
  title: string;
  /** What to do, in a sentence or two. */
  do: string | null;
  minutes: number | null;
  /** The note or file this step opens (vault-relative). */
  vaultPath: string | null;
  /** Heading to jump to in it. */
  heading: string | null;
  /** Sections to read, in order. */
  sections: string[];
  /** What to focus on: key results, exam flags. */
  focus: string[];
  /** Notes to open one by one (the concepts). */
  open: { label: string; vaultPath: string; hint: string | null }[];
  /** Questions to answer without the notes, each with where its answer is. */
  questions: { q: string; vaultPath: string | null; heading: string | null }[];
  /** A web page for the step (the quiz on Blackboard). */
  url: string | null;
}

export interface StudyGuide {
  summary: string | null;
  minutes: number | null;
  steps: StudyGuideStep[];
}

export interface DashboardLink {
  label: string;
  /** "file" (a copy in the vault), "blackboard" or "web". */
  kind: string;
  /** Where the button goes: an https page, or /api/v1/school/file?path=… for a vault file. */
  href: string;
  /** obsidian://open?vault=… for vault files, to open them in Obsidian on the PC. */
  obsidianUrl: string | null;
  /** False when School Manager says the vault file isn't there (yet). */
  available: boolean;
}

export interface DashboardRelated {
  label: string;
  /** "lecture", "concept", "slides", "module"… */
  kind: string;
  /** /api/v1/school/file?path=… (the file itself). */
  href: string;
  /** The note in Tempo's own vault viewer (#/notes?path=…). */
  vaultPath: string | null;
  /** Why it's linked: "covers week 4", "mentions Inductors"… */
  why: string | null;
  obsidianUrl: string | null;
}

export interface DashboardQuota {
  module: string;
  done: number;
  total: number;
  pct: number;
  requiredPct: number | null;
  atRisk: boolean;
}

/** Everything that's going on: what's been checked, what School Manager did, mail, work, quizzes. */
export interface Everything {
  available: boolean;
  generatedAt: string | null;
  sources: DashboardSource[];
  routines: DashboardRoutine[];
  activity: DashboardActivity[];
  mail: EmailContext;
  quotas: DashboardQuota[];
  items: DashboardItem[];
  /** Tempo's own plan problems (work that doesn't fit, travel conflicts…). */
  planIssues: { severity: string; message: string }[];
  /** Red alert: School Manager's plan to catch up on everything missed (null when there isn't one). */
  catchup: CatchupSummary | null;
}

/** School Manager's catch-up plan (dashboard meta.catchup). */
export interface CatchupSummary {
  active: boolean;
  /** "red alert" until the deadline, then "catching up" while anything is left, then "done". */
  phase: string;
  until: string | null;
  startedAt: string | null;
  capMinutesPerDay: number | null;
  total: number;
  done: number;
  minutesTotal: number;
  minutesLeft: number;
  projectedFinish: string | null;
  onTrack: boolean | null;
  /** Item ids in the order to do them. */
  order: string[];
}

/** Something that has ended and needs a done / not done. */
export interface MarkItem {
  /** "class" (a timetabled lecture, tutorial, lab) or "study" (a School Manager study session). */
  kind: 'class' | 'study';
  /** Event id (class) or plan block id (study). */
  id: string;
  key: string;
  title: string;
  module: string | null;
  start: number;
  end: number;
  /** Lecture / tutorial / lab… for classes. */
  session: string | null;
}

/** Changes made in Tempo for School Manager to apply (for-school.json → sm_actions[]). */
export type SmActionType = 'done' | 'reopen' | 'add' | 'attended' | 'missed';

export interface SmAction {
  id: string;
  at: number;
  type: SmActionType;
  itemId: string | null;
  title: string | null;
  due: string | null;
}

// ── The Uni Vault (Notes) ─────────────────────────────────────────────────────────────────────────────────
/** A note in the vault. `type` comes from its frontmatter or folder: lecture, concept, module, transcript… */
export interface VaultNoteRef {
  path: string;
  title: string;
  type: string;
  module: string | null;
}

export interface VaultSummary {
  available: boolean;
  /** "local": read straight from the vault on this PC; "synced": the copy the relay uploads. */
  source: 'local' | 'synced' | null;
  /** When the relay last changed the synced copy. */
  syncedAt: string | null;
  notes: (VaultNoteRef & { folder: string; mtime: string; backlinks: number; tags: string[] })[];
  files: number;
  modules: { code: string; title: string; path: string | null; notes: number }[];
  tags: { tag: string; count: number }[];
}

export interface VaultNote extends VaultNoteRef {
  folder: string;
  mtime: string;
  words: number;
  /** Frontmatter, rendered (links work). */
  properties: { key: string; html: string }[];
  tags: string[];
  /** The note, rendered: Obsidian links, embeds, callouts, maths (KaTeX), tables; mermaid left for the browser. */
  html: string;
  headings: { level: number; text: string; id: string }[];
  backlinks: (VaultNoteRef & { context: string | null })[];
  outgoing: VaultNoteRef[];
  /** Open work in the Everything page that links to this note. */
  work: { id: string; title: string; kind: string | null; module: string | null; due: string | null; status: string }[];
}

export interface VaultGraph {
  nodes: (VaultNoteRef & { deg: number })[];
  /** Index pairs into nodes. */
  links: [number, number][];
}

export interface VaultSearchHit extends VaultNoteRef {
  snippet: string | null;
  /** The heading the match is under, to jump to. */
  heading: string | null;
}
